import { z } from 'zod';

import {
  SkuCaseTransform,
  SkuMissingCodePolicy,
  SkuNumberScope,
} from '@nucleus-commerce/api-client/admin';

/** `^[A-Z0-9]{1,12}$`: the abbreviation SKU templates insert for a value or a product type. */
export const SKU_CODE = /^[A-Z0-9]{1,12}$/;

const wholeNumber = (min: number, max: number, error: string) =>
  z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v) && Number(v) >= min && Number(v) <= max, { error });

/**
 * A SKU rule as the editor edits it. `rootManual` is the "Style numbers are typed by hand"
 * switch: on, the root template is sent as null and whatever was typed is kept in the form so
 * switching back restores it. Counters are the saved rule's, editable forward only.
 */
export const skuRuleFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { error: 'skuRules:validation.nameRequired' })
      .max(120, { error: 'skuRules:validation.nameLength' }),
    rootManual: z.boolean(),
    rootTemplate: z.string().trim().max(200, { error: 'skuRules:validation.templateLength' }),
    variantTemplate: z
      .string()
      .trim()
      .min(1, { error: 'skuRules:validation.variantTemplateRequired' })
      .max(200, { error: 'skuRules:validation.templateLength' }),
    caseTransform: z.enum(SkuCaseTransform),
    numberPadding: wholeNumber(0, 8, 'skuRules:validation.numberPadding'),
    numberScope: z.enum(SkuNumberScope),
    missingCodePolicy: z.enum(SkuMissingCodePolicy),
    maxLength: wholeNumber(8, 64, 'skuRules:validation.maxLength'),
    isDefault: z.boolean(),
    productTypeIds: z.array(z.string()).max(200, { error: 'skuRules:validation.productTypesMax' }),
    counters: z.array(
      z.object({
        scopeKey: z.string(),
        nextNumber: wholeNumber(1, Number.MAX_SAFE_INTEGER, 'skuRules:validation.nextNumber'),
      }),
    ),
  })
  .superRefine((values, ctx) => {
    if (!values.rootManual && values.rootTemplate === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['rootTemplate'],
        message: 'skuRules:validation.rootTemplateRequired',
      });
    }
  });

export type SkuRuleFormInput = z.input<typeof skuRuleFormSchema>;
export type SkuRuleFormValues = z.output<typeof skuRuleFormSchema>;
